// frontend/src/pages/ExtensionAuth.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Stack
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../api/axiosConfig';
import ExtensionIcon from '@mui/icons-material/Extension';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Extension OAuth PKCE Pairing Page
 * Route: /extension-auth?rid=<auth_request_id>
 *
 * Flow:
 * 1. User clicks "Sign In" in extension
 * 2. Extension opens this page in browser with rid parameter
 * 3. If not logged in, redirect to /login with return URL
 * 4. If logged in, show "Pair Device" button
 * 5. User clicks "Pair Device"
 * 6. Call POST /api/auth/extension/approve with rid
 * 7. Show success message
 * 8. Extension completes OAuth flow automatically
 */
export default function ExtensionAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  const [status, setStatus] = useState('loading'); // loading, ready, approving, success, error
  const [error, setError] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);

  const rid = searchParams.get('rid');
  const redirectUri = searchParams.get('redirect_uri');

  useEffect(() => {
    // Validate required parameters
    if (!rid) {
      setStatus('error');
      setError('Missing auth request ID. Please try signing in from the extension again.');
      return;
    }

    if (!redirectUri) {
      setStatus('error');
      setError('Missing redirect URI. Please try signing in from the extension again.');
      return;
    }

    // If auth is still loading, wait
    if (authLoading) {
      return;
    }

    // If not logged in, redirect to login with return URL
    if (!currentUser) {
      const returnUrl = `/extension-auth?rid=${rid}&redirect_uri=${encodeURIComponent(redirectUri)}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
      return;
    }

    // User is logged in, show pairing screen
    setStatus('ready');

  }, [rid, redirectUri, currentUser, authLoading, navigate]);

  const handleApprove = async () => {
    setStatus('approving');
    setError(null);

    try {
      // Call backend to approve auth request
      await axiosInstance.post('/api/auth/extension/approve', { rid });

      setStatus('success');

      // Redirect back to extension with rid
      // Extension will extract rid and exchange for tokens
      if (!redirectUri) {
        throw new Error('Missing redirect_uri parameter');
      }

      const redirectUrl = `${redirectUri}?rid=${rid}`;

      // Small delay to show success message
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1000);

    } catch (err) {
      console.error('Extension pairing approval error:', err);

      setStatus('error');

      if (err.response?.status === 404) {
        setError('Auth request not found or expired. Please try signing in from the extension again.');
      } else if (err.response?.status === 400) {
        setError(err.response.data.error || 'Auth request has already been used.');
      } else {
        setError('Failed to pair device. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    window.close();
  };

  // Loading state
  if (authLoading || status === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center">
              {/* Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: status === 'success' ? 'success.light' : status === 'error' ? 'error.light' : 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {status === 'success' ? (
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.dark' }} />
                ) : status === 'error' ? (
                  <ErrorIcon sx={{ fontSize: 48, color: 'error.dark' }} />
                ) : (
                  <ExtensionIcon sx={{ fontSize: 48, color: 'primary.dark' }} />
                )}
              </Box>

              {/* Title */}
              <Typography variant="h4" component="h1" align="center" fontWeight={600}>
                {status === 'success' ? 'Device Paired!' : status === 'error' ? 'Pairing Failed' : 'Pair Browser Extension'}
              </Typography>

              {/* Status Messages */}
              {status === 'ready' && (
                <>
                  <Typography variant="body1" align="center" color="text.secondary">
                    Click "Pair Device" to authorize the Blackmount browser extension to access your account.
                  </Typography>
                  <Typography variant="body2" align="center" color="text.secondary">
                    You can revoke access anytime from your account settings.
                  </Typography>
                </>
              )}

              {status === 'approving' && (
                <Stack spacing={2} alignItems="center">
                  <CircularProgress size={40} />
                  <Typography variant="body1" align="center">
                    Pairing device...
                  </Typography>
                </Stack>
              )}

              {status === 'success' && (
                <>
                  <Typography variant="body1" align="center" color="success.main">
                    Your browser extension has been paired successfully!
                  </Typography>
                  <Typography variant="body2" align="center" color="text.secondary">
                    You can now close this window and return to the extension.
                  </Typography>
                  <Typography variant="caption" align="center" color="text.secondary">
                    This window will close automatically in 3 seconds...
                  </Typography>
                </>
              )}

              {/* Error Alert */}
              {status === 'error' && error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}

              {/* Action Buttons */}
              {status === 'ready' && (
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApprove}
                    size="large"
                    startIcon={<ExtensionIcon />}
                  >
                    Pair Device
                  </Button>
                </Stack>
              )}

              {status === 'error' && (
                <Button
                  variant="outlined"
                  onClick={() => window.close()}
                  size="large"
                >
                  Close Window
                </Button>
              )}

              {/* User Info */}
              {currentUser && status !== 'error' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, width: '100%' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Signed in as:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {currentUser.email}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Box sx={{ mt: 3, px: 2 }}>
          <Typography variant="caption" align="center" color="text.secondary" display="block">
            🔒 This authorization uses industry-standard OAuth 2.0 with PKCE for secure device pairing.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
